import { Trans, t } from '@lingui/macro'
import { List, Stack } from '@mui/material'
import { Box, Typography, Divider, Alert, ListItem, ListItemText, ListItemIcon, Avatar, Checkbox } from '@mui/material'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import Chip from '@mui/material/Chip'
import { deepOrange, deepPurple } from '@mui/material/colors'

import { Parent, Patient } from '@/core/models/patient.model'

import { useParentInfo } from '../model/useParentInfo'
import { useFormContext, useWatch } from 'react-hook-form'
import { MergePatientsFormData } from '../model/useMergePatientsForm'

type MergeParentsListProps = {
  primaryPatient: Patient
  secondaryPatient: Patient
}

export function MergeParentsList({ primaryPatient, secondaryPatient }: MergeParentsListProps) {
  const { allParents, hasSharedParents, canSelectParents } = useParentInfo({ primaryPatient, secondaryPatient })

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Trans>Parents</Trans>
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Alert severity="info" sx={{ mb: 2 }}>
        {!canSelectParents ? (
          <Trans>The two patients have the same parents. All parents will be kept in the merged patient record.</Trans>
        ) : hasSharedParents ? (
          <Trans>
            The two patients share at least one parent. Check the parents you want to keep, each selected parent will appear only once in the merged record.
          </Trans>
        ) : (
          <Trans>Check the parents you want to keep in the merged patient record.</Trans>
        )}
      </Alert>
      <List>
        {allParents.map((parent) => (
          <ParentItem
            key={parent._id}
            disabled={!canSelectParents}
            parent={parent}
            source={primaryPatient?.parents.some((p: Parent) => p._id === parent._id) ? 'primary' : 'secondary'}
          />
        ))}
      </List>
    </Box>
  )
}

function ParentItem({ disabled, parent, source }: { disabled: boolean; parent: Parent; source: 'primary' | 'secondary' }) {
  const rhForm = useFormContext<MergePatientsFormData>()
  const selectedParents = useWatch({ control: rhForm.control, name: 'parentIds' })

  const isChecked = selectedParents.includes(parent._id)

  return (
    <ListItem
      secondaryAction={
        !disabled ? (
          <Checkbox
            checked={isChecked}
            onChange={() => {
              rhForm.setValue('parentIds', isChecked ? selectedParents.filter((id) => id !== parent._id) : [...selectedParents, parent._id])
            }}
          />
        ) : undefined
      }
      sx={{ borderRadius: 1, mb: 1, bgcolor: !disabled && isChecked ? 'action.selected' : 'transparent' }}
    >
      <ListItemIcon>
        <Avatar sx={{ bgcolor: source === 'primary' ? deepOrange[500] : deepPurple[500] }}>
          <PersonAddIcon />
        </Avatar>
      </ListItemIcon>
      <ListItemText
        primary={`${parent.firstname || ''} ${parent.lastname || ''}`}
        secondary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={source === 'primary' ? t`Primary` : t`Secondary`} size="small" color={source} />
            {parent.email && <Typography variant="caption">{parent.email}</Typography>}
          </Stack>
        }
      />
    </ListItem>
  )
}
